import React, { useContext } from 'react'
import styled from '@emotion/styled'
import dayjs from 'dayjs'
import { makeStyles } from '@material-ui/styles'
import { makeImgResizerUrl } from '@jelly/utils'
import { object, shape, string } from 'prop-types'

import Typography from '@material-ui/core/Typography'
import Hidden from '@material-ui/core/Hidden'
import { ContentSocialBlock, SiteConfigContext } from '@jelly/components'

const useStyles = () =>
	makeStyles(
		(theme) => {
			return {
				Header: {
					fontSize: '1.8rem',

					[theme.breakpoints.up('md')]: {
						fontSize: '2.25rem',
						marginRight: '1rem',
					},
				},
			}
		},
		{ name: 'linkBuildingHeader' }
	)

const HeaderWrapper = styled.header`
	display: flex;
	flex-direction: column;
	height: auto;
	position: relative;
	overflow: hidden;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		flex-direction: row;
	}
`
const LogoContainer = styled.div`
	height: auto;
	margin-bottom: 1rem;
	max-width: 150px;
	width: 100%;
`
const HeaderInfo = styled.div`
	background-repeat: no-repeat;
	background-size: cover;
	color: ${({ theme }) => theme.palette.common.white};
	display: flex;
	height: auto;
	position: relative;
	width: 100%;

	${({ backgroundSrc, theme }) =>
		backgroundSrc
			? `
			&.lazyloaded {
				background: linear-gradient(0deg, rgba(0, 59, 92, .9), rgba(0, 59, 92, .9)), url(${backgroundSrc});
			}
		`
			: `background-color: ${theme.palette.primary.main};`}

	${({ theme }) => theme.MQ.M} {
		width: 60%;
	}
	${({ theme }) => theme.MQ.L} {
		width: 50%;
	}
`
const AuthorByline = styled.div`
	font-size: 1rem;
	margin-bottom: 1rem;
	margin-right: 1rem;
	opacity: 0.8;

	${({ theme }) => theme.MQ.XL} {
		font-size: 1.25rem;
	}
`
const InfoContext = styled.div`
	margin: 3rem auto;
	position: relative;
	width: 90%;

	${({ theme }) => theme.MQ.M} {
		max-width: 600px;
		margin: 3rem 0 3rem auto;
	}
`
const HeaderImage = styled.img`
	display: none;
	height: 100%;
	object-fit: cover;
	position: absolute;
	right: 0;

	${({ theme }) => theme.MQ.M} {
		display: block;
		width: 40%;
	}
	${({ theme }) => theme.MQ.L} {
		width: 50%;
	}
`
const SocialShareWrapper = styled.div`
	margin: 1rem 1rem 0 auto;
	right: 0;

	${({ theme }) => theme.MQ.M} {
		margin: 1rem;
		bottom: 0;
		position: absolute;
	}
`
const shouldShowCompany = (company, type) => {
	return /staff/i.test(type) && company
}

HeaderInfo.propTypes = {
	author: shape({
		name: string,
		slug: string,
		user: object,
	}),
	canonical: string,
	lastModified: string,
	published: string,
	thumbnail: object,
	title: string,
}

/**
 * Header for Link Building Template
 * Consists of thumbnail hero image, title, byline and social links
 *
 * @param {Object} props.author :: object of author data
 * @param {String} props.canonical :: current url path of the page
 * @param {String} props.lastModified :: date saved from ccm
 * @param {String} props.published :: date saved from ccm
 * @param {Object} props.thumbnail :: data for thumbnail image
 * @param {String} props.title :: title of current article saved from ccm
 */
export function Header({ author, canonical, lastModified, published, thumbnail, title }) {
	const {
		siteComponents: { SiteLogo },
		siteConstants: { LINK_BUILDING_HERO = '' },
	} = useContext(SiteConfigContext)
	const classes = useStyles({})()
	const lastModifiedDate = dayjs(lastModified)
	const isUpdated = lastModifiedDate.isAfter(published)

	const bylineDate = `${isUpdated ? 'Updated ' : ''}${lastModifiedDate.format('MMM DD, YYYY')}`

	return (
		<HeaderWrapper>
			<HeaderInfo
				className="lazyload"
				backgroundSrc={
					LINK_BUILDING_HERO && makeImgResizerUrl({ src: LINK_BUILDING_HERO, method: 'w', w: 800 })
				}
			>
				<InfoContext>
					<LogoContainer>
						<SiteLogo color="#FFFFFF" />
					</LogoContainer>
					<Typography className={classes.Header} variant="h1">
						{title}
					</Typography>
					<AuthorByline>
						By {author.name}
						{author.user?.profile && (
							<>
								{author.user.profile?.jobTitle && <>, {author.user.profile.jobTitle}</>}
								{shouldShowCompany(author.user.profile?.company, author.type) && (
									<>{` at ${author.user.profile.company}`}</>
								)}
							</>
						)}{' '}
						| {bylineDate}
					</AuthorByline>
				</InfoContext>
			</HeaderInfo>
			<Hidden smDown>
				<HeaderImage
					className="lazyload"
					data-src={makeImgResizerUrl({ src: thumbnail.path, method: 'w', w: 800 })}
				></HeaderImage>
			</Hidden>
			<SocialShareWrapper>
				<ContentSocialBlock canonical={canonical} title={title} variant="neutral" />
			</SocialShareWrapper>
		</HeaderWrapper>
	)
}
